import React, { useState } from 'react';
import { Booking } from '../../types/booking';

interface BookingExportMenuProps {
  bookings: Booking[];
  selectedBookings?: Booking[];
}

const BookingExportMenu: React.FC<BookingExportMenuProps> = ({
  bookings,
  selectedBookings = []
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportToCSV = (data: Booking[]) => {
    const headers = [
      'Booking Reference',
      'Customer Name',
      'Customer Email',
      'Customer Phone',
      'Vehicle',
      'Pickup Date',
      'Return Date',
      'Status',
      'Payment Status',
      'Total Amount',
      'Paid Amount',
      'Created At'
    ];

    const rows = data.map(booking => [
      booking.bookingReference,
      booking.customer ? `${booking.customer.firstName} ${booking.customer.lastName}` : 'N/A',
      booking.customer?.email || 'N/A',
      booking.customer?.phone || (booking.customer as any)?.contact || 'N/A',
      booking.car?.name || 'N/A',
      new Date(booking.pickUpDate).toLocaleDateString(),
      new Date(booking.returnDate).toLocaleDateString(),
      booking.status,
      booking.paymentStatus,
      booking.totalAmount,
      booking.paidAmount,
      new Date(booking.createdAt).toLocaleString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bookings_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = async (data: Booking[]) => {
    // This would integrate with a PDF library like jsPDF
    alert('PDF export functionality would be implemented here with jsPDF library');
  };

  const printBookings = (data: Booking[]) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bookings Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #4F46E5; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Bookings Report</h1>
          <p>Generated on: ${new Date().toLocaleString()}</p>
          <p>Total Bookings: ${data.length}</p>
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Customer</th>
                <th>Vehicle</th>
                <th>Pickup Date</th>
                <th>Return Date</th>
                <th>Status</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${data.map(booking => `
                <tr>
                  <td>${booking.bookingReference}</td>
                  <td>${booking.customer ? `${booking.customer.firstName} ${booking.customer.lastName}` : 'N/A'}</td>
                  <td>${booking.car?.name || 'N/A'}</td>
                  <td>${new Date(booking.pickUpDate).toLocaleDateString()}</td>
                  <td>${new Date(booking.returnDate).toLocaleDateString()}</td>
                  <td>${booking.status}</td>
                  <td>$${booking.totalAmount.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <button onclick="window.print()" style="margin-top: 20px; padding: 10px 20px; background: #4F46E5; color: white; border: none; border-radius: 5px; cursor: pointer;">Print</button>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleExport = async (format: 'csv' | 'pdf' | 'print') => {
    setExporting(true);
    const dataToExport = selectedBookings.length > 0 ? selectedBookings : bookings;

    try {
      switch (format) {
        case 'csv':
          exportToCSV(dataToExport);
          break;
        case 'pdf':
          await exportToPDF(dataToExport);
          break;
        case 'print':
          printBookings(dataToExport);
          break;
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
      setShowMenu(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
        disabled={exporting}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        {exporting ? 'Exporting...' : 'Export'}
      </button>

      {showMenu && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowMenu(false)}
          ></div>
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden">
            <div className="py-1">
              <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-200">
                {selectedBookings.length > 0
                  ? `Export ${selectedBookings.length} selected booking${selectedBookings.length !== 1 ? 's' : ''}`
                  : `Export all ${bookings.length} bookings`}
              </div>

              <button
                onClick={() => handleExport('csv')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
              >
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export as CSV
              </button>

              <button
                onClick={() => handleExport('pdf')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
              >
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Export as PDF
              </button>

              <button
                onClick={() => handleExport('print')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
              >
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Report
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BookingExportMenu;
